import React, { useState } from "react";

import {
  ChevronDown,
  FilterLeft,
  Search,
  HeartFill,
  InfoCircle,
} from "react-bootstrap-icons";
import Dropdown from "react-bootstrap/esm/Dropdown";
import NoFavoritesImg from "./images/no-favorites.png";
import {
  Col,
  Container,
  Row,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import {
  useRemoveFavoriteMutation,
  useFavoritesQuery,
} from "../../graphql/favorites.hooks";
import { FavoriteSortField, Order } from "../../graphql/schema/types";
import Rating from "../reviews/Rating";
import Pagination from "../../components/pagination";
import ReserveIcon from "./images/ReserveIcon";
import WorkSchedule from "./WorkSchedule";

const Favorites = () => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const [activeFilterLabel, setActiveFilterLabel] = useState(
    t("booking.newest")
  );
  const [sortBy, setSortBy] = useState<FavoriteSortField>(
    FavoriteSortField.DateAdded
  );
  const [sortOrder, setSortOrder] = useState<Order>(Order.Desc);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearch, setAppliedSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const limit = 12;
  const { data, loading } = useFavoritesQuery({
    variables: {
      page: currentPage,
      perPage: limit,
      search: appliedSearch,
      sortBy,
      sortOrder,
    },
  });
  const favorites = data?.user?.favorites?.items;
  const favoritesData = data?.user?.favorites;
  const pageCount = favoritesData?.pageCount || 0;
  const totalItems = favoritesData?.totalCount || 0;
  const firstItemIndex = favoritesData
    ? (favoritesData.currentPage - 1) * favoritesData.itemsPerPage + 1
    : 0;
  const lastItemIndex = favoritesData
    ? Math.min(
        favoritesData.currentPage * favoritesData.itemsPerPage,
        totalItems
      )
    : 0;

  const [removeFavorite] = useRemoveFavoriteMutation({
    refetchQueries: ["Favorites"],
  });

  const ME_BOOKING_LINK = process.env.REACT_APP_ME_BOOKING_URL;

  const [removingId, setRemovingId] = useState<number | null>(null);

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (
    label: string,
    field: FavoriteSortField,
    order: Order
  ) => {
    setSortBy(field);
    setSortOrder(order);
    setActiveFilterLabel(label);
    setCurrentPage(1);
  };

  const handleRemoveFavorite = async (restaurantId: number) => {
    setRemovingId(restaurantId);
    try {
      await removeFavorite({ variables: { restaurantId } });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      <Container>
        <h1 className="mb-5 fs-2 mt-5 mt-lg-0">{t("favorites.title")}</h1>
        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : favorites?.length === 0 && appliedSearch.trim() === "" ? (
          <div className="bg-white rounded-4 py-5">
            <div className="col-lg-6 mx-auto py-5 text-center">
              <img src={NoFavoritesImg} alt="No Favorites Img" />
              <p className="fw-bold fs-4">{t("favorites.noFavorites")}</p>
              <p className="mb-4 text-gray col-lg-6 mx-auto">
                {t("favorites.noFavoritesDescription")}
              </p>
              <div className="col-lg-4 mx-auto">
                <a
                  href={`${
                    lng === "en"
                      ? process.env.REACT_APP_ME_BOOKING_URL
                      : `${process.env.REACT_APP_ME_BOOKING_URL}/${lng}`
                  }`}
                  className="btn btn-primary py-3 fw-bold text-white w-100"
                >
                  {t("favorites.browseRestaurants")}
                </a>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4 d-lg-flex justify-content-between">
              <div className="col-lg-3 mb-2 mb-lg-0">
                <div className="col-xxl-9 col-12">
                  <div className="bg-white rounded-3 position-relative">
                    <input
                      type="text"
                      className="form-control border-0 py-2"
                      id="search"
                      placeholder={t("booking.search-placeholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                    <Search
                      className="position-absolute translate-middle-y top-50 end-0 me-3"
                      style={{ cursor: "pointer" }}
                      onClick={handleSearch}
                    />
                  </div>
                </div>
              </div>
              <div>
                <Dropdown className="bg-white rounded-3">
                  <Dropdown.Toggle
                    id="dropdown-basic"
                    variant="link"
                    className="no-toggle text-dark text-decoration-none py-2 d-flex justify-content-between align-items-center w-100"
                  >
                    <div>
                      <FilterLeft className="me-3" />
                      {activeFilterLabel}
                    </div>
                    <ChevronDown className="ms-2" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      active={activeFilterLabel === t("booking.newest")}
                      onClick={() =>
                        handleSort(
                          t("booking.newest"),
                          FavoriteSortField.DateAdded,
                          Order.Desc
                        )
                      }
                    >
                      {t("booking.newest")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={activeFilterLabel === t("booking.oldest")}
                      onClick={() =>
                        handleSort(
                          t("booking.oldest"),
                          FavoriteSortField.DateAdded,
                          Order.Asc
                        )
                      }
                    >
                      {t("booking.oldest")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={activeFilterLabel === t("booking.name1")}
                      onClick={() =>
                        handleSort(
                          t("booking.name1"),
                          FavoriteSortField.Name,
                          Order.Asc
                        )
                      }
                    >
                      {t("booking.name1")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={activeFilterLabel === t("booking.name2")}
                      onClick={() =>
                        handleSort(
                          t("booking.name2"),
                          FavoriteSortField.Name,
                          Order.Desc
                        )
                      }
                    >
                      {t("booking.name2")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            {favorites?.length === 0 ? (
              <div className="bg-white rounded-4 py-5 text-center">
                <p className="fw-bold fs-4">{t("booking.noSearchResults")}</p>
              </div>
            ) : null}

            <Row className="g-4">
              {favorites?.map((item) => (
                <Col key={item?.restaurant.id} xs={12} lg={3}>
                  <div className="rounded-4 overflow-hidden bg-white h-100 d-flex flex-column justify-content-between restaurant-item">
                    <div className="position-relative">
                      <a
                        className="d-block text-decoration-none text-dark"
                        href={item?.restaurant.link}
                      >
                        <div className="favorite-card">
                          <img
                            className="img-fluid h-100 w-100 object-fit-cover"
                            src={item?.restaurant.logo}
                            alt={`Restaurant Image ${item?.restaurant.id}`}
                          />
                        </div>
                      </a>
                      <div className="p-4">
                        <div className="d-flex fs-7 mb-1 justify-content-between">
                          <div className="text-gray d-flex align-items-center">
                            <div className="mx-1 d-flex">
                              <span className="me-2">
                                {item?.restaurant.rating?.toFixed(1)}
                              </span>
                              <Rating ratingData={item?.restaurant.rating} />
                            </div>
                            (<u>{item?.restaurant.reviewCount}</u>)
                          </div>

                          <button
                            className="text-primary border-0 bg-transparent p-2"
                            disabled={removingId === item?.restaurant.id}
                            onClick={() =>
                              handleRemoveFavorite(item?.restaurant.id)
                            }
                          >
                            {removingId === item?.restaurant.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <HeartFill className="fs-6" />
                            )}
                          </button>
                        </div>
                        <a
                          className="d-block text-decoration-none text-dark"
                          href={item?.restaurant.link}
                        >
                          <p className="fs-6 fw-bold mb-1">
                            {item?.restaurant.name}
                          </p>
                          <p className="mb-1 text-gray">
                            <small>
                              {item?.restaurant.cuisines.map((cuisine) => (
                                <span key={cuisine.id}>{cuisine.name}</span>
                              ))}
                              {item?.restaurant.cuisines.length > 1 && " / "}
                              {item?.restaurant.cuisines.length > 0 &&
                              item?.restaurant.types.length > 0
                                ? " • "
                                : ""}
                              {item?.restaurant.types.map((type, index) => (
                                <span key={type.id}>
                                  {type.name}
                                  {index < item?.restaurant.types.length - 1 &&
                                    " / "}
                                </span>
                              ))}

                              {item?.restaurant.priceRange && (
                                <>
                                  {(item?.restaurant.cuisines.length > 0 ||
                                    item?.restaurant.types.length > 0) &&
                                    " • "}
                                  {item?.restaurant.priceRange}
                                </>
                              )}
                            </small>
                          </p>
                          <div className="d-flex align-items-center">
                            {item?.restaurant.isOpenNow === true ? (
                              <span className="text-success fs-7">
                                {t("favorites.openUntil", {
                                  time: item?.restaurant.openUntil,
                                })}
                              </span>
                            ) : (
                              <span className="text-danger fs-7">
                                {t("favorites.closed")}
                              </span>
                            )}
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip>
                                  <p className="fw-bold text-start mb-2">
                                    {t("favorites.workingHours")}
                                  </p>
                                  <WorkSchedule
                                    schedules={
                                      item?.restaurant.workSchedulesFormated ??
                                      []
                                    }
                                  />
                                </Tooltip>
                              }
                            >
                              <InfoCircle
                                className="ms-1"
                                style={{ cursor: "pointer" }}
                              />
                            </OverlayTrigger>
                          </div>
                        </a>
                      </div>
                    </div>
                    <div className="px-4 pb-4">
                      <a
                        href={
                          ME_BOOKING_LINK +
                          lng +
                          "/reservation/" +
                          item?.restaurant.slug
                        }
                        className="btn btn-primary fw-bold w-100 rounded-3 text-white d-flex align-items-center justify-content-center"
                      >
                        <ReserveIcon />
                        <span className="ms-2">{t("favorites.reserve")}</span>
                      </a>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
            <Pagination
              t={t}
              pages={pageCount}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              firstItemIndex={firstItemIndex}
              lastItemIndex={lastItemIndex}
              totalItems={totalItems}
              limit={limit}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default Favorites;
